// Daily wellness metrics — mirrors health-service HealthMetricDTO (camelCase JSON).
// One record per day; manual self-entry (source = 'manual', set server-side).

export interface HealthMetric {
  // Read-only — populated on responses, ignored on input.
  id?: string;
  keycloakId?: string;
  source?: string;
  createdAt?: string;
  updatedAt?: string;

  metricDate: string; // ISO date (YYYY-MM-DD), required

  // Heart
  restingHr?: number | null;
  hrvMs?: number | null;
  maxHr?: number | null;

  // Sleep
  sleepMinutes?: number | null;
  sleepDeepMinutes?: number | null;
  sleepLightMinutes?: number | null;
  sleepRemMinutes?: number | null;
  sleepAwakeMinutes?: number | null;
  sleepScore?: number | null;

  // Stress / body / respiration
  stressAvg?: number | null;
  bodyBatteryHigh?: number | null;
  bodyBatteryLow?: number | null;
  spo2Avg?: number | null;
  respirationAvg?: number | null;

  // Activity
  steps?: number | null;
  caloriesActive?: number | null;
  caloriesResting?: number | null;
  vo2max?: number | null;
  intensityMinutes?: number | null;

  note?: string | null;
}

export type HealthMetricKey = Exclude<
  keyof HealthMetric,
  'id' | 'keycloakId' | 'source' | 'createdAt' | 'updatedAt' | 'metricDate' | 'note'
>;

/** A radial gauge in the cockpit. `max` is the value that fills the full 270° arc. */
export interface HealthDialDef {
  key: HealthMetricKey;
  label: string;
  unit: string;
  color: string;
  min: number;
  max: number;
  decimals: number;
  /** Verdict shown under the value, by fraction of range (0..1). */
  verdict: (value: number) => string;
}

/** Daily steps goal — one shared constant for gauges, rings and ticker copy. */
export const STEPS_GOAL = 10000;

export const HEALTH_DIALS: ReadonlyArray<HealthDialDef> = [
  {
    key: 'sleepScore',
    label: 'sleep score',
    unit: '/ 100',
    color: '#8E62D9',
    min: 0,
    max: 100,
    decimals: 0,
    verdict: (v) => (v >= 80 ? 'excellent' : v >= 60 ? 'good' : v >= 40 ? 'fair' : 'poor'),
  },
  {
    key: 'restingHr',
    label: 'resting HR',
    unit: 'bpm',
    color: '#FF5C5C',
    min: 30,
    max: 110,
    decimals: 0,
    verdict: (v) => (v < 60 ? 'low · fit' : v < 75 ? 'normal' : v < 90 ? 'elevated' : 'high'),
  },
  {
    key: 'hrvMs',
    label: 'HRV',
    unit: 'ms',
    color: '#B89CFF',
    min: 0,
    max: 120,
    decimals: 0,
    verdict: (v) => (v >= 60 ? 'high' : v >= 30 ? 'balanced' : 'low'),
  },
  {
    key: 'maxHr',
    label: 'max HR',
    unit: 'bpm',
    color: '#FF3B1F',
    min: 100,
    max: 220,
    decimals: 0,
    verdict: () => 'peak',
  },
  {
    key: 'stressAvg',
    label: 'stress · avg',
    unit: '/ 100',
    color: '#FF9847',
    min: 0,
    max: 100,
    decimals: 0,
    verdict: (v) => (v < 25 ? 'low' : v < 50 ? 'moderate' : v < 75 ? 'high' : 'very high'),
  },
  {
    key: 'bodyBatteryHigh',
    label: 'body battery',
    unit: '/ 100',
    color: '#CBFF47',
    min: 0,
    max: 100,
    decimals: 0,
    verdict: (v) => (v >= 70 ? 'charged' : v >= 40 ? 'moderate' : 'drained'),
  },
  {
    key: 'spo2Avg',
    label: 'SpO₂',
    unit: '%',
    color: '#00DCC8',
    min: 85,
    max: 100,
    decimals: 0,
    verdict: (v) => (v >= 95 ? 'normal' : v >= 90 ? 'low' : 'very low'),
  },
  {
    key: 'respirationAvg',
    label: 'breath rate',
    unit: 'br/min',
    color: '#7FE7B0',
    min: 6,
    max: 30,
    decimals: 1,
    verdict: (v) => (v >= 12 && v <= 20 ? 'steady' : 'irregular'),
  },
  {
    key: 'steps',
    label: 'steps · today',
    unit: 'steps',
    color: '#CBFF47',
    min: 0,
    max: STEPS_GOAL,
    decimals: 0,
    verdict: (v) => `${Math.round((v / STEPS_GOAL) * 100)}% of goal`,
  },
  {
    key: 'vo2max',
    label: 'VO₂ max',
    unit: 'ml/kg',
    color: '#5B8DEF',
    min: 20,
    max: 70,
    decimals: 1,
    verdict: (v) => (v >= 50 ? 'excellent' : v >= 40 ? 'good' : 'fair'),
  },
];

/** Sleep-stage minutes for the distribution band. */
export const SLEEP_STAGES: ReadonlyArray<{
  key: HealthMetricKey;
  label: string;
  color: string;
}> = [
  { key: 'sleepDeepMinutes', label: 'deep', color: '#3D3CB8' },
  { key: 'sleepRemMinutes', label: 'rem', color: '#8E62D9' },
  { key: 'sleepLightMinutes', label: 'light', color: '#5B8DEF' },
  { key: 'sleepAwakeMinutes', label: 'awake', color: '#FFB547' },
];

export interface HealthFieldDef {
  key: HealthMetricKey;
  label: string;
  unit: string;
  min: number;
  max: number;
  step: number;
}

export interface HealthFieldGroup {
  group: string;
  fields: ReadonlyArray<HealthFieldDef>;
}

// Ranges mirror the backend bean-validation constraints on HealthMetricDTO.
export const HEALTH_FIELD_GROUPS: ReadonlyArray<HealthFieldGroup> = [
  {
    group: 'Heart',
    fields: [
      { key: 'restingHr', label: 'Resting HR', unit: 'bpm', min: 20, max: 250, step: 1 },
      { key: 'hrvMs', label: 'HRV', unit: 'ms', min: 0, max: 500, step: 1 },
      { key: 'maxHr', label: 'Max HR', unit: 'bpm', min: 20, max: 250, step: 1 },
    ],
  },
  {
    group: 'Sleep',
    fields: [
      { key: 'sleepScore', label: 'Sleep score', unit: '/100', min: 0, max: 100, step: 1 },
      { key: 'sleepMinutes', label: 'Total sleep', unit: 'min', min: 0, max: 1440, step: 1 },
      { key: 'sleepDeepMinutes', label: 'Deep', unit: 'min', min: 0, max: 1440, step: 1 },
      { key: 'sleepRemMinutes', label: 'REM', unit: 'min', min: 0, max: 1440, step: 1 },
      { key: 'sleepLightMinutes', label: 'Light', unit: 'min', min: 0, max: 1440, step: 1 },
      { key: 'sleepAwakeMinutes', label: 'Awake', unit: 'min', min: 0, max: 1440, step: 1 },
    ],
  },
  {
    group: 'Vitals',
    fields: [
      { key: 'stressAvg', label: 'Stress avg', unit: '/100', min: 0, max: 100, step: 1 },
      { key: 'bodyBatteryHigh', label: 'Battery high', unit: '/100', min: 0, max: 100, step: 1 },
      { key: 'bodyBatteryLow', label: 'Battery low', unit: '/100', min: 0, max: 100, step: 1 },
      { key: 'spo2Avg', label: 'SpO₂ avg', unit: '%', min: 0, max: 100, step: 0.1 },
      { key: 'respirationAvg', label: 'Respiration', unit: 'br/min', min: 0, max: 100, step: 0.1 },
    ],
  },
  {
    group: 'Activity',
    fields: [
      { key: 'steps', label: 'Steps', unit: '', min: 0, max: 100000, step: 1 },
      { key: 'caloriesActive', label: 'Active cal', unit: 'kcal', min: 0, max: 20000, step: 1 },
      { key: 'caloriesResting', label: 'Resting cal', unit: 'kcal', min: 0, max: 20000, step: 1 },
      { key: 'vo2max', label: 'VO₂ max', unit: 'ml/kg', min: 0, max: 100, step: 0.1 },
      { key: 'intensityMinutes', label: 'Intensity', unit: 'min', min: 0, max: 1440, step: 1 },
    ],
  },
];
